import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import * as Rx from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  BackendDeleteErrorTypes,
  BackendDeletePayloadTypes,
  BackendDeleteReturnTypes,
  BackendFetchPayloadTypes,
  BackendFetchReturnTypes,
  BackendPushErrorTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  BackendSetErrorTypes,
  BackendSetPayloadTypes,
  BackendSetReturnTypes,
  BackendUpdateErrorTypes,
  BackendUpdatePayloadTypes,
  BackendUpdateReturnTypes,
} from '../backend.model';
import { Collections, SearchCollections } from '../collections.model';
import {
  CollectionActions,
  DeleteRequestPayload,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { ApiFetchResponse, ApiHttp } from './api-http.service';
import { ResponseData } from './datastore.interface';
import { assertNever, isDefined, RecursivePartial } from './helpers';
import { DatastoreMissingModuleError } from './missing-module-error';
import { Ordering, SingleOrdering } from './query';
import { pluckItemFromRawStore } from './store.helpers';
import {
  Path,
  RawQuery,
  Reference,
  SearchQuery,
  StoreState,
} from './store.model';

export interface BackendSuccessResponse {
  readonly status: 'success';
}

type ExtractId<T> = T extends { readonly id: any } ? T['id'] : undefined;

export interface BackendPushSuccessResponse<K extends keyof Collections>
  extends BackendSuccessResponse {
  readonly id: ExtractId<BackendPushReturnTypes[K]>;
}

export type BackendAllErrorCodes = ErrorCodeApi | 'UNKNOWN_ERROR';

export interface BackendErrorResponse<E> {
  readonly status: 'error';
  readonly errorCode: E;
  readonly requestId?: string;
}

export type BackendPushErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendPushErrorTypes[K]>;
export type BackendSetErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendSetErrorTypes[K]>;
export type BackendUpdateErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendUpdateErrorTypes[K]>;
export type BackendDeleteErrorResponse<
  K extends keyof Collections
> = BackendErrorResponse<BackendDeleteErrorTypes[K]>;

export type BackendPushResponse<K extends keyof Collections> =
  | BackendPushSuccessResponse<K>
  | BackendPushErrorResponse<K>;
export type BackendSetResponse<K extends keyof Collections> =
  | BackendSuccessResponse
  | BackendSetErrorResponse<K>;
export type BackendUpdateResponse<K extends keyof Collections> =
  | BackendSuccessResponse
  | BackendUpdateErrorResponse<K>;
export type BackendDeleteResponse<K extends keyof Collections> =
  | BackendSuccessResponse
  | BackendDeleteErrorResponse<K>;

/** Key-value pairs passed to the backend via query parameters in the URL */
export interface Params {
  readonly [key: string]:
    | string
    | number
    | boolean
    | ReadonlyArray<number | undefined>
    | ReadonlyArray<string | undefined>
    | undefined;
}

interface BackendFetchGetRequest {
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
}

interface BackendFetchPostRequest<
  K extends keyof (Collections & SearchCollections)
> {
  readonly payload: BackendFetchPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
  readonly method: 'POST';
}

export type BackendFetchRequest<
  K extends keyof (Collections & SearchCollections)
> = BackendFetchGetRequest | BackendFetchPostRequest<K>;

type FetchRequestFactory<K extends keyof Collections> = (
  authUid: string,
  ids: ReadonlyArray<string> | undefined,
  query: RawQuery<Collections[K]> | undefined,
  order: Ordering<K> | undefined,
) => BackendFetchRequest<K>;

export interface BackendPushRequest<K extends keyof Collections> {
  readonly payload: BackendPushPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
}

type PushRequestFactory<K extends keyof Collections> = (
  authUid: string,
  entity: Collections[K],
  extra: { readonly [index: string]: string | number } | undefined,
) => BackendPushRequest<K>;

export interface BackendSetRequest<K extends keyof Collections> {
  readonly payload: BackendSetPayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
}

type SetRequestFactory<K extends keyof Collections> = (
  authUid: string,
  entity: Collections[K],
) => BackendSetRequest<K>;

export interface BackendUpdateRequest<K extends keyof Collections> {
  readonly payload: BackendUpdatePayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT';
}

type UpdateRequestFactory<K extends keyof Collections> = (
  authUid: string,
  delta: RecursivePartial<Collections[K]>,
  originalEntity: Collections[K],
) => BackendUpdateRequest<K>;

export interface BackendDeleteRequest<K extends keyof Collections> {
  readonly payload: BackendDeletePayloadTypes[K];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT' | 'DELETE';
}

type DeleteRequestFactory<K extends keyof Collections> = (
  authUid: string,
  id: string | number,
  originalEntity: Collections[K],
) => BackendDeleteRequest<K>;

export interface Backend<K extends keyof Collections> {
  readonly fetch?: FetchRequestFactory<K>;
  readonly push?: PushRequestFactory<K>;
  readonly set?: SetRequestFactory<K>;
  readonly update?: UpdateRequestFactory<K>;
  readonly remove?: DeleteRequestFactory<K>;
  readonly defaultOrder: SingleOrdering<K> | Ordering<K>;
  readonly maxBatchSize?: number;
}

export const BACKEND_DEFAULT_BATCH_SIZE = 100;

export interface BackendSearch<K extends keyof SearchCollections> {
  search(query: SearchQuery): BackendFetchRequest<K>;
}

export type BackendConfigs = { [K in keyof Collections]?: Backend<K> } &
  { [K in keyof SearchCollections]?: BackendSearch<K> };

@Injectable()
export class StoreBackend {
  private backendConfigs: BackendConfigs = {};

  constructor(private store$: Store<StoreState>, private apiHttp: ApiHttp) {}

  defaultOrder<K extends keyof Collections>(collection: K): Ordering<K> {
    const config = this.backendConfigs[collection] as Backend<K> | undefined;
    if (!config) {
      throw new DatastoreMissingModuleError(collection);
    }
    return Array.isArray(config.defaultOrder)
      ? config.defaultOrder
      : ([config.defaultOrder] as Ordering<K>);
  }

  batchSize<K extends keyof Collections>(ref: Reference<K>): number {
    const config = this.backendConfigs[ref.path.collection];
    return (config && config.maxBatchSize) || BACKEND_DEFAULT_BATCH_SIZE;
  }

  fetch<K extends keyof Collections>(
    ref: Reference<K>,
  ): Rx.Observable<ApiFetchResponse<K>> {
    const { path, query, order } = ref;
    const type = path.collection;
    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.fetch;
      }),
      filter(isDefined),
      map(fetch => fetch(path.authUid, path.ids, query, order)),
      switchMap(request => this.apiHttp.fetch(request, query)),
    );
  }

  search<K extends keyof SearchCollections>(
    collection: K,
    query$: Rx.Observable<SearchQuery>,
  ): Rx.Observable<BackendFetchReturnTypes[K]> {
    return query$.pipe(
      map(q => {
        const config = this.backendConfigs[collection];
        if (!config) {
          throw new DatastoreMissingModuleError(collection);
        }
        return config.search(q);
      }),
      switchMap(request => this.apiHttp.fetch(request)),
      map(
        response =>
          (response.status === 'success'
            ? response.result
            : []) as BackendFetchReturnTypes[K],
      ), // FIXME: Error handling?
    );
  }

  push<K extends keyof Collections>(
    ref: Reference<K>,
    object: Collections[K],
    extra?: { readonly [index: string]: string | number },
  ): Rx.Observable<BackendPushResponse<K>> {
    const { path, query } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.push;
      }),
      filter(isDefined),
      map(push => {
        const request = push(path.authUid, object, extra);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_PUSH`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) =>
        this.apiHttp.post(request).pipe(map(result => ({ payload, result }))),
      ),
      map(({ payload, result }) =>
        this.sendActions({ type: 'PUSH', payload }, path, query, result),
      ),
    );
  }

  set<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    object: Collections[K],
  ): Rx.Observable<BackendSetResponse<K>> {
    const { path, query } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.set;
      }),
      filter(isDefined),
      withLatestFrom(this.store$),
      map(([set, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = set(path.authUid, object);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_SET`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) =>
        this.apiHttp.post(request).pipe(map(result => ({ payload, result }))),
      ),
      map(({ payload, result }) =>
        this.sendActions({ type: 'SET', payload }, path, query, result),
      ),
    );
  }

  update<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    delta: RecursivePartial<Collections[K]>,
  ): Rx.Observable<BackendUpdateResponse<K>> {
    const { path } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.update;
      }),
      filter(isDefined),
      withLatestFrom(this.store$),
      map(([update, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = update(path.authUid, delta, originalObject);
        return {
          request,
          payload: {
            type,
            ref,
            delta,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_UPDATE`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) => {
        switch (request.method) {
          case 'POST':
            return this.apiHttp
              .post(request)
              .pipe(map(result => ({ payload, result })));
          case 'PUT':
            return this.apiHttp
              .put(request)
              .pipe(map(result => ({ payload, result })));
          default:
            return assertNever(request.method);
        }
      }),
      map(({ payload, result }) =>
        this.sendActions(
          { type: 'UPDATE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  delete<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
  ): Rx.Observable<BackendDeleteResponse<K>> {
    const { path } = ref;
    const type = path.collection;

    return Rx.of(this.backendConfigs[type] as Backend<K> | undefined).pipe(
      map(config => {
        if (!config) {
          throw new DatastoreMissingModuleError(type);
        }
        return config.remove;
      }),
      filter(isDefined),
      withLatestFrom(this.store$),
      map(([del, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = del(path.authUid, id, originalObject);
        return {
          request,
          payload: {
            type,
            ref,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_DELETE`, payload };
        this.store$.dispatch(action);
      }),
      switchMap(({ request, payload }) => {
        switch (request.method) {
          case 'POST':
            return this.apiHttp
              .post(request)
              .pipe(map(result => ({ payload, result })));
          case 'PUT':
            return this.apiHttp
              .put(request)
              .pipe(map(result => ({ payload, result })));
          case 'DELETE':
            return this.apiHttp
              .delete(request)
              .pipe(map(result => ({ payload, result })));
          default:
            return assertNever(request.method);
        }
      }),
      map(({ payload, result }) =>
        this.sendActions(
          { type: 'DELETE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'PUSH';
      readonly payload: PushRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendPushReturnTypes[K], BackendPushErrorTypes[K]>,
  ): BackendPushResponse<K>;
  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'SET';
      readonly payload: SetRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendSetReturnTypes[K], BackendSetErrorTypes[K]>,
  ): BackendSetResponse<K>;
  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'UPDATE';
      readonly payload: UpdateRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>,
  ): BackendUpdateResponse<K>;
  private sendActions<K extends keyof Collections>(
    baseAction: {
      readonly type: 'DELETE';
      readonly payload: DeleteRequestPayload<K>;
    },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>,
  ): ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>;
  private sendActions<K extends keyof Collections>(
    baseAction:
      | { readonly type: 'PUSH'; readonly payload: PushRequestPayload<K> }
      | { readonly type: 'SET'; readonly payload: SetRequestPayload<K> }
      | { readonly type: 'UPDATE'; readonly payload: UpdateRequestPayload<K> }
      | { readonly type: 'DELETE'; readonly payload: DeleteRequestPayload<K> },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data:
      | ResponseData<BackendPushReturnTypes[K], BackendPushErrorTypes[K]>
      | ResponseData<BackendSetReturnTypes[K], BackendSetErrorTypes[K]>
      | ResponseData<BackendUpdateReturnTypes[K], BackendUpdateErrorTypes[K]>
      | ResponseData<BackendDeleteReturnTypes[K], BackendDeleteErrorTypes[K]>,
  ):
    | BackendPushResponse<K>
    | BackendSetResponse<K>
    | BackendUpdateResponse<K>
    | BackendDeleteResponse<K> {
    switch (data.status) {
      case 'success': {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? ({
                type: 'API_PUSH_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  object: baseAction.payload.object,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>)
            : baseAction.type === 'SET'
            ? ({
                type: 'API_SET_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  object: baseAction.payload.object,
                  originalObject: baseAction.payload.originalObject,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>)
            : baseAction.type === 'UPDATE'
            ? ({
                type: 'API_UPDATE_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  delta: baseAction.payload.delta,
                  originalObject: baseAction.payload.originalObject,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>)
            : ({
                type: 'API_DELETE_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  originalObject: baseAction.payload.originalObject,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              } as CollectionActions<K>);
        this.store$.dispatch(action);
        return baseAction.type === 'PUSH'
          ? ({
              status: 'success',
              id: data.result && data.result.id,
            } as BackendSuccessResponse)
          : { status: 'success' };
      }
      default: {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_ERROR',
                payload: baseAction.payload,
              }
            : baseAction.type === 'SET'
            ? {
                type: 'API_SET_ERROR',
                payload: baseAction.payload,
              }
            : baseAction.type === 'UPDATE'
            ? {
                type: 'API_UPDATE_ERROR',
                payload: baseAction.payload,
              }
            : {
                type: 'API_DELETE_ERROR',
                payload: baseAction.payload,
              };
        this.store$.dispatch(action);
        return data;
      }
    }
  }

  addFeature<K extends keyof Collections>(
    collectionName: K,
    requestFactory: Backend<K>,
  ): void;

  addFeature<K extends keyof SearchCollections>(
    collectionName: K,
    requestFactory: BackendSearch<K>,
  ): void;

  addFeature<K extends keyof (Collections & SearchCollections)>(
    collectionName: K,
    requestFactory: any,
  ): void {
    this.backendConfigs[collectionName] = requestFactory;
  }
}

export const BACKEND_COLLECTIONS = new InjectionToken<ReadonlyArray<string>>(
  'Backend Collections',
);

export const BACKEND_CONFIGS = new InjectionToken<SearchConfigsProvider>(
  'Backend Configs',
);

type BackendConfigFactory<K extends keyof Collections> = () => Backend<K>;

type BackendSearchConfigFactory<
  K extends keyof SearchCollections
> = () => BackendSearch<K>;

export type SearchConfigsProvider = ReadonlyArray<Backend<keyof Collections>> &
  ReadonlyArray<BackendSearch<keyof SearchCollections>>;

@NgModule({})
export class BackendRootModule {}

@NgModule({})
export class BackendFeatureModule<
  K extends keyof (Collections & SearchCollections)
> {
  constructor(
    storeBackend: StoreBackend,
    @Inject(BACKEND_COLLECTIONS) backendCollections: ReadonlyArray<K>,
    @Inject(BACKEND_CONFIGS) backendConfigs: SearchConfigsProvider,
  ) {
    backendCollections.map((collectionName, index) => {
      storeBackend.addFeature(collectionName as any, backendConfigs[index]); // FIXME: any type
    });
  }
}

@NgModule({})
export class BackendModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: BackendRootModule,
      providers: [StoreBackend],
    };
  }

  static forFeature<K extends keyof Collections>(
    collectionName: K,
    configFactory: BackendConfigFactory<K>,
  ): ModuleWithProviders;

  static forFeature<K extends keyof SearchCollections>(
    collectionName: K,
    configFactory: BackendSearchConfigFactory<K>,
  ): ModuleWithProviders;

  static forFeature<K extends keyof (Collections & SearchCollections)>(
    collectionName: K,
    configFactory: any,
  ): ModuleWithProviders {
    return {
      ngModule: BackendFeatureModule,
      providers: [
        {
          provide: BACKEND_COLLECTIONS,
          multi: true,
          useValue: collectionName,
        },
        {
          provide: BACKEND_CONFIGS,
          multi: true,
          useFactory: configFactory,
        },
      ],
    };
  }
}
